import { Alert, Card, Spin, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import { fetchCrowsNestLadderCensus } from 'src/utils/crowsNestApi'
import { mapLadderCensus } from 'src/utils/crowsNestLadderCensus'
import {
  CROWS_NEST_CENSUS_ROW_TIPS,
  CROWS_NEST_TOOLTIPS,
} from 'src/utils/crowsNestTooltips'
import { crowsNestErrorMessage } from 'src/utils/crowsNestErrors'
import styled from 'styled-components/macro'
import CrowsNestTipLabel from './CrowsNestTipLabel'

const CENSUS_PLAN = 'Free'

/**
 * “6 of how many” — how many Free venues sit below the supplier milestone,
 * dormant, or still unadopted on recipes / stock / par.
 */
const CrowsNestLadderCensus = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [census, setCensus] = useState(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchCrowsNestLadderCensus({ plan: CENSUS_PLAN })
        if (cancelled) return
        setCensus(mapLadderCensus(response))
      } catch (err) {
        if (!cancelled) {
          setError(
            crowsNestErrorMessage(
              err,
              "Couldn't load the ladder census. Try again in a moment.",
            ),
          )
          setCensus(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <StatsCard
      data-testid='crows-nest-ladder-census'
      title={
        <TitleBlock>
          <TitleRow>
            <span>Ladder census</span>
            <Tooltip title={CROWS_NEST_TOOLTIPS.ladderCensusSection}>
              <InfoCircleOutlined />
            </Tooltip>
          </TitleRow>
          {census ? (
            <ScopeLabel data-testid='census-scope-label'>
              {census.scopeLabel}
            </ScopeLabel>
          ) : null}
        </TitleBlock>
      }
    >
      {error && !census ? (
        <Alert type='error' showIcon message={error} />
      ) : null}

      {loading && !census ? (
        <LoadingWrap>
          <Spin />
        </LoadingWrap>
      ) : null}

      {census ? (
        <>
          <PrimaryBlock>
            <PrimaryLabel>
              <CrowsNestTipLabel
                label='Free venues'
                tip={CROWS_NEST_TOOLTIPS.censusVenues}
              />
            </PrimaryLabel>
            <PrimaryValue data-testid='census-total-venues'>
              {census.venues}
            </PrimaryValue>
          </PrimaryBlock>

          <RowGrid>
            {census.rows.map(row => (
              <Stat key={row.key} data-testid={`census-row-${row.key}`}>
                <StatLabel>
                  <CrowsNestTipLabel
                    label={row.label}
                    tip={CROWS_NEST_CENSUS_ROW_TIPS[row.key]}
                  />
                </StatLabel>
                <StatValue data-testid={`census-count-${row.key}`}>
                  {row.ofVenues}
                </StatValue>
                <StatShare data-testid={`census-share-${row.key}`}>
                  {row.shareLabel}
                </StatShare>
              </Stat>
            ))}
          </RowGrid>
        </>
      ) : null}
    </StatsCard>
  )
}

const StatsCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
  }
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TitleRow = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;

  .anticon {
    color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
    font-size: 14px;
  }
`

const ScopeLabel = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 12px;
  font-weight: 500;
`

const LoadingWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 120px;
`

const PrimaryBlock = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md || '16px'};
`

const PrimaryLabel = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 14px;
  font-weight: 500;
`

const PrimaryValue = styled.div`
  font-size: 48px;
  font-weight: 700;
  line-height: 1.1;
`

const RowGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md || '16px'};
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
`

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 13px;
`

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 600;
`

const StatShare = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 12px;
`

export default CrowsNestLadderCensus
